import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import { t } from "ttag";

import { TrustpilotStars } from "@/assets/onboarding/trustpilot-stars";
import { env } from "@/config/env";

/** Counts down from `seconds` and returns a "MM:SS" string (stops at 0). */
export const useCountdown = (seconds: number) => {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const minutes = Math.floor(remaining / 60);
  const secs = remaining % 60;

  return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

/** Blue "Your Discount is Reserved for MM:SS" banner. */
export const FunnelTimerBanner = ({ time }: { time: string }) => (
  <div className="rounded-xl bg-secondary-50 px-6 py-4 text-center text-lg font-semibold text-neutral-900">
    {t`Your Discount is Reserved for`}{" "}
    <span className="text-secondary-500">{time}</span>
  </div>
);

/**
 * Trustpilot rating row: the official Trustpilot stars, the score, and the live
 * review count. The count comes from `VITE_TRUSTPILOT_REVIEWS_COUNT` (set per
 * environment) so the number stays in sync with the rest of the app.
 */
export const FunnelTrustpilot = ({
  label = t`Excellent`,
  score = "4.7",
}: {
  label?: string;
  score?: string;
}) => (
  <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
    <TrustpilotStars full={false} className="w-28" />
    <span className="text-sm font-bold text-neutral-900">
      {label} {score}
    </span>
    <span className="text-sm text-neutral-600">
      {env.VITE_TRUSTPILOT_REVIEWS_COUNT} {t`reviews`}
    </span>
  </div>
);

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`size-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

interface FunnelAccordionProps extends PropsWithChildren {
  title: string;
  defaultOpen?: boolean;
}

/** Collapsible section ("All Plans Include", "How It Works"). */
export const FunnelAccordion = ({
  title,
  defaultOpen = true,
  children,
}: FunnelAccordionProps) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-2xl bg-white/60 px-5 py-4">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between text-left text-xl font-bold text-neutral-900"
      >
        {title}
        <ChevronIcon open={open} />
      </button>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
};

/** Accepted card / wallet logos row (text placeholders for the brand logos). */
export const FunnelPaymentIcons = () => (
  <div className="flex flex-wrap items-center gap-2">
    {["VISA", "MC", "AMEX", "DISCOVER", "Pay"].map((brand) => (
      <span
        key={brand}
        className="flex h-8 min-w-12 items-center justify-center rounded border border-neutral-200 bg-white px-2 text-xs font-bold text-neutral-700"
      >
        {brand}
      </span>
    ))}
  </div>
);

const CardIcon = () => (
  <svg viewBox="0 0 24 24" className="size-7" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2.5" fill="#4f6bd8" />
    <rect x="2" y="8" width="20" height="3" fill="#0c2d63" />
    <rect x="5" y="14" width="6" height="2" rx="1" fill="white" />
  </svg>
);

const ApplePayPill = () => (
  <span className="flex h-7 items-center gap-0.5 rounded-md bg-black px-2 text-xs font-semibold text-white">
    <svg viewBox="0 0 24 24" className="size-3" fill="white" aria-hidden="true">
      <path d="M17 12c0-2 1.6-3 1.7-3-1-1.4-2.4-1.6-2.9-1.6-1.2-.1-2.4.7-3 .7-.6 0-1.6-.7-2.6-.7-1.3 0-2.6.8-3.3 2-1.4 2.5-.4 6.1 1 8.1.7 1 1.4 2.1 2.4 2 1-.1 1.3-.6 2.5-.6s1.5.6 2.5.6 1.7-1 2.3-2c.7-1.1 1-2.2 1-2.2s-1.9-.8-1.9-2.9ZM15 6.3c.5-.7.9-1.6.8-2.6-.8 0-1.8.5-2.4 1.2-.5.6-1 1.5-.8 2.5.9.1 1.8-.4 2.4-1.1Z" />
    </svg>
    Pay
  </span>
);

const GooglePayPill = () => (
  <span className="flex h-7 items-center rounded-md border border-neutral-200 bg-white px-2 text-xs font-bold text-neutral-700">
    <span className="text-[#4285F4]">G</span>
    <span className="ml-0.5">Pay</span>
  </span>
);

interface FunnelPaymentMethodSelectorProps {
  value: "card" | "bnpl";
  onSelect: (value: "card" | "bnpl") => void;
}

/**
 * placeholder-style payment method selector: a "Card & Wallets" box and a "Buy Now,
 * Pay Later" box. The selected box is a raised white card; the other sits flat
 * on the grey container. Selecting a box notifies the parent so it can toggle
 * the card inputs or trigger the BNPL (Klarna) flow.
 */
export const FunnelPaymentMethodSelector = ({
  value,
  onSelect,
}: FunnelPaymentMethodSelectorProps) => (
  <div className="flex items-stretch gap-3 rounded-2xl bg-secondary-50 p-3">
    <button
      type="button"
      onClick={() => onSelect("card")}
      className={`flex-1 rounded-xl px-4 py-5 text-center transition ${
        value === "card" ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <p className="text-lg font-bold text-neutral-900">{t`Card & Wallets`}</p>
      <div className="mt-3 flex items-center justify-center gap-2">
        <CardIcon />
        <ApplePayPill />
        <GooglePayPill />
      </div>
    </button>

    <button
      type="button"
      onClick={() => onSelect("bnpl")}
      className={`flex-1 rounded-xl px-4 py-5 text-center transition ${
        value === "bnpl" ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <p className="text-lg font-bold text-neutral-900">{t`Buy Now, Pay Later`}</p>
      <div className="mt-3 flex items-center justify-center">
        <span className="rounded-md bg-[#ffd6e7] px-2 py-1 text-xs font-bold text-neutral-900">
          Klarna.
        </span>
      </div>
    </button>
  </div>
);
